import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Avatar,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  MenuItem
} from '@mui/material';
import { Add, Delete, Edit, CloudUpload, Close, Person } from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar } from '../../utils/showMessage';
import Loader from '../../components/Loader';

const textFieldStyle = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
    },
  }
};

const BoardMembersManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/board-members');
      setMembers(res.data);
    } catch (e) {
      console.error(e);
      snackbar('خطا در دریافت لیست');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditing({ name: '', title: '', description: '', image: '', order: 0, isActive: true });
    setOpen(true);
  };

  const handleEdit = (member) => {
    setEditing({ ...member });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing._id) {
        await api.patch(`/board-members/${editing._id}`, editing);
        snackbar('با موفقیت بروزرسانی شد');
      } else {
        await api.post('/board-members', editing);
        snackbar('با موفقیت ایجاد شد');
      }
      setOpen(false);
      fetchMembers();
    } catch (e) {
      console.error(e);
      snackbar('خطا در ذخیره اطلاعات');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try {
      await api.delete(`/board-members/${id}`);
      snackbar('با موفقیت حذف شد');
      fetchMembers();
    } catch (e) {
      console.error(e);
      snackbar('خطا در حذف');
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/welfare/upload-image', fd);
      setEditing({ ...editing, image: `/uploads/documents/${res.data.filename}` });
    } catch (e) {
      console.error(e);
      snackbar('خطا در آپلود تصویر');
    }
  };

  if (loading && members.length === 0) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'stretch' : 'center'}
        gap={2}
        mb={3}
      >
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary">مدیریت اعضای هیئت مدیره</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAdd}
          fullWidth={isMobile}
          sx={{ borderRadius: 3, px: 3 }}
        >
          عضو جدید
        </Button>
      </Box>

      <Grid container spacing={2}>
        {members.map(m => (
          <Grid item xs={12} sm={6} md={4} key={m._id}>
            <Card sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'relative' }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" gap={2} alignItems="center">
                  <Avatar
                    src={m.image ? (m.image.startsWith('http') ? m.image : `${process.env.REACT_APP_FILE_BASE_URL}${m.image}`) : "/assets/img/user1.jpg"}
                    sx={{ width: 70, height: 70, borderRadius: 2, border: '2px solid #fff', boxShadow: 2 }}
                  />
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">{m.name}</Typography>
                    <Typography variant="body2" color="textSecondary">{m.title}</Typography>
                    <Typography variant="caption" color="primary" sx={{ bgcolor: 'primary.lighter', px: 1, borderRadius: 1 }}>ترتیب نمایش: {m.order}</Typography>
                  </Box>
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <IconButton color="primary" onClick={() => handleEdit(m)} size="small" sx={{ bgcolor: 'primary.lighter' }}><Edit fontSize="small" /></IconButton>
                    <IconButton color="error" onClick={() => handleDelete(m._id)} size="small" sx={{ bgcolor: 'error.lighter' }}><Delete fontSize="small" /></IconButton>
                  </Box>
                </Box>
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {m.description || 'بدون توضیحات'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">{editing?._id ? 'ویرایش عضو' : 'عضو جدید'}</Typography>
          {isMobile && <IconButton onClick={() => setOpen(false)} size="small"><Close /></IconButton>}
        </DialogTitle>
        <DialogContent dividers>

          <Box pt={1} display="flex" flexDirection="column" gap={0.5}>
            <TextField
              fullWidth
              select
              label="دوره"
              value={editing?.duration || ''}
              onChange={(e) => setEditing({ ...editing, duration: e.target.value })}
              sx={textFieldStyle}
            >

              <MenuItem key="d_9"
                value="دوره نهم"
              >دوره نهم</MenuItem>
              <MenuItem key="d_8"
                value="دوره هشتم"
              >دوره هشتم</MenuItem>
              <MenuItem key="d_7"
                value="دوره هفتم"
              >دوره هفتم</MenuItem>
              <MenuItem key="d_6"
                value="دوره ششم"
              >دوره ششم</MenuItem>
              <MenuItem
                value="دوره پنجم"
              >دوره پنجم</MenuItem>
              <MenuItem
                value="دوره چهارم"
              >دوره چهارم</MenuItem>
              <MenuItem
                value="دوره سوم"
              >دوره سوم</MenuItem>
              <MenuItem
                value="دوره دوم"
              >دوره دوم</MenuItem>
              <MenuItem
                value="دوره اول"
              >دوره اول</MenuItem>

            </TextField>
            <TextField
              fullWidth
              label="نام و نام خانوادگی"
              value={editing?.name || ''}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              sx={textFieldStyle}
            />
            <TextField
              fullWidth
              multiline
              label="سمت"
              value={editing?.title || ''}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              sx={textFieldStyle}
            />
            <TextField
              fullWidth
              label="توضیحات"
              multiline
              rows={3}
              value={editing?.description || ''}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              sx={textFieldStyle}
            />
            <TextField
              fullWidth
              type="number"
              label="ترتیب نمایش"
              value={editing?.order || 0}
              onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) })}
              sx={textFieldStyle}
            />

            <Box sx={{ ...textFieldStyle, display: 'flex', alignItems: 'center', height: '56px', px: 2, border: '1px solid rgba(0,0,0,0.1)', borderRadius: '12px', mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editing?.isActive ?? true}
                    onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                  />
                }
                label="فعال و نمایش داده شود"
              />
            </Box>

            <Box
              sx={{
                p: 2,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'grey.50',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} sx={{ borderRadius: 2 }}>
                بارگذاری تصویر عضو
                <input hidden type="file" onChange={(e) => uploadImage(e.target.files[0])} />
              </Button>
              {editing?.image && (
                <Avatar
                  src={editing.image.startsWith('http') ? editing.image : `${process.env.REACT_APP_FILE_BASE_URL}${editing.image}`}
                  variant="rounded"
                  sx={{ width: 100, height: 100, borderRadius: 2, boxShadow: 2, border: '3px solid #fff' }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button fullWidth={isMobile} onClick={() => setOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>انصراف</Button>
          <Button fullWidth={isMobile} variant="contained" onClick={handleSave} sx={{ borderRadius: 2 }}>ذخیره</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BoardMembersManagement;

